import { Box, Skeleton } from "@mui/material";
import { cardSx, type TonalTokens } from "../../../polish/tokens";
import { dayHeadingSx } from "./headings";
import { PLOT_HEIGHT } from "./MetricBars";
import {
  NOTE_ROW_HEIGHT,
  PANEL_PADDING,
  REPORT_ROW_COLUMNS,
  REPORT_ROW_GAP,
  REPORT_ROWS_GAP,
} from "./ResultsCard";

interface ResultsCardSkeletonProps {
  tokens: TonalTokens;
  /** Locked: still grey, never shimmering, so it reads as a shape and not a load. */
  still?: boolean;
  /** The hub's row: the results card with Since report beside the company, and the notes card beside it. */
  notes?: boolean;
}

type Animation = false | undefined;

interface ChartsSkeletonProps {
  animation: Animation;
}

/** EPS and revenue: a heading, a plot and its bar labels each. */
const ChartsSkeleton = ({ animation }: ChartsSkeletonProps) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      columnGap: { xs: 2, md: 4 },
      mt: { xs: 2, md: 2.5 },
    }}
  >
    {[0, 1].map((at) => (
      <Box key={at}>
        <Skeleton animation={animation} width="30%" height={20} />
        <Skeleton
          animation={animation}
          variant="rounded"
          height={PLOT_HEIGHT}
          sx={{ mt: 0.5, borderRadius: "8px" }}
        />
        <Skeleton
          animation={animation}
          width="40%"
          height={14}
          sx={{ mt: 0.75, mx: "auto" }}
        />
      </Box>
    ))}
  </Box>
);

interface HubCardSkeletonProps {
  tokens: TonalTokens;
  animation: Animation;
}

/** The hub row's final shape: the results card (identity, Since report, the charts) beside the notes card. */
const HubCardSkeleton = ({ tokens, animation }: HubCardSkeletonProps) => (
  <Box
    data-testid="results-card-skeleton"
    sx={{
      display: "grid",
      gridTemplateColumns: REPORT_ROW_COLUMNS,
      gap: REPORT_ROW_GAP,
    }}
  >
    <Box sx={{ ...cardSx(tokens), ...PANEL_PADDING }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          columnGap: { xs: 1.5, md: 2.5 },
          rowGap: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            flex: { xs: "1 0 100%", sm: "0 1 auto" },
          }}
        >
          <Skeleton
            animation={animation}
            variant="rounded"
            width={40}
            height={40}
            sx={{ borderRadius: "8px", flex: "none" }}
          />
          <Box>
            <Skeleton animation={animation} width={96} height={22} />
            <Skeleton animation={animation} width={128} height={18} />
          </Box>
        </Box>
        <Box
          sx={{
            display: { xs: "none", sm: "block" },
            width: "1px",
            height: 36,
            backgroundColor: tokens.hairline,
          }}
        />
        <Box>
          <Skeleton animation={animation} width={52} height={16} />
          <Skeleton animation={animation} width={32} height={28} />
        </Box>
        <Box
          sx={{ width: "1px", height: 36, backgroundColor: tokens.hairline }}
        />
        <Box>
          <Skeleton animation={animation} width={76} height={16} />
          <Skeleton animation={animation} width={60} height={28} />
        </Box>
      </Box>
      <ChartsSkeleton animation={animation} />
    </Box>
    <Box
      data-testid="card-notes-skeleton"
      sx={{ ...cardSx(tokens), ...PANEL_PADDING }}
    >
      <Skeleton animation={animation} width="58%" height={24} />
      <Box sx={{ mt: 1.25 }}>
        {[0, 1, 2, 3].map((at) => (
          <Box
            key={at}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              height: NOTE_ROW_HEIGHT,
              borderTop: `1px solid ${tokens.hairline}`,
            }}
          >
            <Skeleton animation={animation} width="32%" height={20} />
            <Skeleton animation={animation} width="26%" height={20} />
            <Skeleton
              animation={animation}
              width="22%"
              height={20}
              sx={{ ml: "auto" }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

/** A results card's final shape: on the hub its row of cards, on the report page a title and the two charts. */
export const ResultsCardSkeleton = ({
  tokens,
  still = false,
  notes = false,
}: ResultsCardSkeletonProps) => {
  const animation = still ? false : undefined;
  if (notes) return <HubCardSkeleton tokens={tokens} animation={animation} />;
  return (
    <Box
      data-testid="results-card-skeleton"
      sx={{ ...cardSx(tokens), ...PANEL_PADDING }}
    >
      <Skeleton animation={animation} width="24%" height={26} />
      <ChartsSkeleton animation={animation} />
    </Box>
  );
};

interface FeedSkeletonProps {
  tokens: TonalTokens;
  count: number;
}

/** The feed while its first page loads: a day heading and report card shapes. */
export const FeedSkeleton = ({ tokens, count }: FeedSkeletonProps) => (
  <Box data-testid="feed-skeleton">
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
      <Skeleton width={96} sx={dayHeadingSx(tokens)} />
      <Box sx={{ flex: 1, height: "1px", backgroundColor: tokens.hairline }} />
    </Box>
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: REPORT_ROWS_GAP }}
    >
      {Array.from({ length: count }, (_, at) => (
        <ResultsCardSkeleton key={at} tokens={tokens} notes />
      ))}
    </Box>
  </Box>
);
